import React from 'react';
import { createRoot } from 'react-dom/client';
import * as ReactDOMShim from 'react-dom';
// React 19 removed the legacy ReactDOM top-level APIs; some deps still call them:
// react-slick -> findDOMNode, react-confirm-alert -> render/unmountComponentAtNode.
// Minimal compat shim so those components render instead of throwing.
{
  const rd: any = (ReactDOMShim as any).default ?? ReactDOMShim;
  if (rd && typeof rd.findDOMNode !== 'function') {
    rd.findDOMNode = (node: any) => (node && node.nodeType ? node : null);
  }
  // One root per container, reused across re-renders — calling createRoot twice on the
  // same node warns and leaks the previous root. Weak so detached nodes stay collectable.
  const legacyRoots = new WeakMap<Element | DocumentFragment, any>();
  if (rd && typeof rd.render !== 'function') {
    rd.render = (element: any, container: Element) => {
      let root = legacyRoots.get(container);
      if (!root) {
        root = createRoot(container);
        legacyRoots.set(container, root);
      }
      root.render(element);
    };
  }
  if (rd && typeof rd.unmountComponentAtNode !== 'function') {
    rd.unmountComponentAtNode = (container: Element) => {
      const root = legacyRoots.get(container);
      if (!root) return false;
      // createRoot().unmount() throws if called while React is rendering; the legacy API
      // was sync-safe, and callers (react-confirm-alert) unmount from event handlers.
      Promise.resolve().then(() => root.unmount());
      legacyRoots.delete(container);
      return true;
    };
  }
}
import ReactGA from 'react-ga4';
import { Provider } from 'react-redux';
import { configureStore } from './store/configureStore';
import { ApolloProvider } from '@apollo/client/react';
import App from './App';
import { router } from './router';
import { initSentry } from './sentry';
import { installChunkReloadHandler } from './utils/lazyWithRetry';
import { apolloClient } from './apollo/client';

initSentry();

// Recover tabs still running a pre-deploy build, whose content-hashed chunks
// no longer exist on the server.
installChunkReloadHandler();

// Validate environment variables
const requiredEnvVars = ['VITE_API_ENDPOINT'];
requiredEnvVars.forEach((varName) => {
  if (!(window?.env?.[varName] || import.meta.env[varName])) {
    throw new Error(`Missing required environment variable: ${varName}`);
  }
});

export { BACKEND_URI } from "./config";

export const store = configureStore();

// Initialize GA4
const GA_MEASUREMENT_ID = window?.env?.GA_MEASUREMENT_ID || import.meta.env.VITE_GA_MEASUREMENT_ID;
if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID);

  // Send initial pageview
  ReactGA.send({
    hitType: 'pageview',
    page: window.location.pathname + window.location.search,
  });

  // Track subsequent pageviews. The router notifies on every state transition,
  // so only report when the path actually changed.
  let lastTrackedPath = window.location.pathname;
  router.subscribe((state) => {
    const { pathname } = state.location;
    if (pathname === lastTrackedPath) return;
    lastTrackedPath = pathname;
    ReactGA.set({ page: pathname });
    ReactGA.send('pageview');
  });
}

// Debounced resize handler
let resizeTimeout: number | undefined;
const debouncedResize = () => {
  if (resizeTimeout !== undefined) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = window.setTimeout(() => {
    store.dispatch({
      type: 'metadata/SET_SCREEN_RESIZE',
      screenSize: window.innerWidth,
    });
  }, 100);
};

window.addEventListener('resize', debouncedResize);

// Cleanup function
const cleanup = () => {
  window.removeEventListener('resize', debouncedResize);
};

// Expose store when run in Cypress
if (window.Cypress) {
  window.store = store;
  window.cleanupAnalytics = cleanup;
}

// Create root and render app
const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');


// Persist root across HMR reloads
let root: ReturnType<typeof createRoot>;

if (!(container as any)._reactRoot) {
  root = createRoot(container);
  (container as any)._reactRoot = root;
} else {
  root = (container as any)._reactRoot;
}

root.render(
  <React.StrictMode>
    <ApolloProvider client={apolloClient}>
      <Provider store={store}>
        <App />
      </Provider>
    </ApolloProvider>
  </React.StrictMode>
);

// Cleanup on unmount (for testing purposes)
if (import.meta.env.MODE === 'test') {
  cleanup();
}